import React, { createContext, useContext, useReducer } from "react";
import {
  COLLECTION_ID,
  Space,
  SpaceTrackRow,
} from "@highnote/server/src/core/entities";
import { getSpaceTrackRowId } from "@highnote/server/src/core/shared-util";
import { QueryConstraint } from "firebase/firestore";
import {
  WatcherComponentId,
  WatcherState,
  useWatcherManagementV2,
} from "../watchers/useWatcherV2";

type GlobalSpaceTracksAction = {
  type: "SET_SPACE_TRACK_ROWS";
  payload: {
    spaceId: Space["id"];
    rows: SpaceTrackRow[];
  };
};

type GlobalSpaceTracksState = {
  // Map of the composite `${spaceId}_${trackId}` row id (see
  // `getSpaceTrackRowId`, `core/shared-util.ts`) to its space-tracks-v1 row.
  spaceTrackRows: Map<string, SpaceTrackRow>;
  // Map of spaceId to the trackIds of that space's space-tracks-v1 rows.
  rowChildTracks: Map<Space["id"], Array<SpaceTrackRow["trackId"]>>;
};

function spaceTracksReducer(
  state: GlobalSpaceTracksState,
  action: GlobalSpaceTracksAction,
): GlobalSpaceTracksState {
  switch (action.type) {
    case "SET_SPACE_TRACK_ROWS": {
      const { spaceId, rows } = action.payload;
      const newSpaceTrackRows = new Map(state.spaceTrackRows);

      // Prune this space's previous row ids that are absent from the new
      // snapshot BEFORE writing the incoming rows below — otherwise a track
      // that was unshared from this space (its row deleted server-side)
      // would leave a permanent stale entry in `spaceTrackRows` even though
      // `rowChildTracks` correctly stops listing it. We only touch ids
      // previously recorded for THIS spaceId, so other spaces' rows are
      // untouched.
      const previousRowChildTrackIds = state.rowChildTracks.get(spaceId);
      if (previousRowChildTrackIds) {
        previousRowChildTrackIds.forEach((trackId) => {
          newSpaceTrackRows.delete(getSpaceTrackRowId(spaceId, trackId));
        });
      }

      // `action.payload` returns a new array of rows, so we reset this
      // space's child row ids here to ensure we're not adding duplicates,
      // or keeping stale data (mirrors ADD_CHILD_TRACKS in
      // `store/tracks/useGlobalTracks.tsx`).
      const newRowChildTrackIds = new Set<string>();

      rows.forEach((row) => {
        newRowChildTrackIds.add(row.trackId);
        newSpaceTrackRows.set(
          getSpaceTrackRowId(row.spaceId, row.trackId),
          row,
        );
      });

      const newRowChildTracks = new Map(state.rowChildTracks);
      newRowChildTracks.set(spaceId, Array.from(newRowChildTrackIds));

      return {
        ...state,
        spaceTrackRows: newSpaceTrackRows,
        rowChildTracks: newRowChildTracks,
      };
    }
    default:
      return state;
  }
}

type ManageGlobalSpaceTracksWatchersArgs = {
  action: "attach" | "detach";
  watcherKey: string;
  componentId: WatcherComponentId;
  /**
   * Required on attach, and never empty: `space-tracks-v1` only ALLOWS a
   * list pinned to one `spaceId` (rules), so an unfiltered query is a
   * silent deny that renders as an empty space. `constraintsFor`
   * (`useChildEntityWatchers`) builds the right shape.
   */
  constraints?: QueryConstraint[];
  limit?: number;
  setEntities: (entities: SpaceTrackRow[]) => void;
};

type GlobalSpaceTracksContext = {
  globalSpaceTracks: GlobalSpaceTracksState;
  globalSpaceTracksWatchers: WatcherState;
  globalSpaceTracksDispatch: React.Dispatch<GlobalSpaceTracksAction>;
  manageGlobalSpaceTracksWatchers: (
    args: ManageGlobalSpaceTracksWatchersArgs,
  ) => Promise<void>;
};

const GlobalSpaceTracksContext = createContext<GlobalSpaceTracksContext>(
  null as GlobalSpaceTracksContext,
);

export const GlobalSpaceTracksContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [globalSpaceTracks, globalSpaceTracksDispatch] = useReducer(
    spaceTracksReducer,
    {
      spaceTrackRows: new Map(),
      rowChildTracks: new Map(),
    },
  );

  // A DEDICATED useWatcherManagementV2 instance — deliberately NOT shared
  // with useGlobalTracks (`store/tracks/useGlobalTracks.tsx`). That hook's
  // registry is a single Map keyed only by `watcherKey` (typically a
  // spaceId), with no regard for collectionId. If this store attached
  // through the SAME instance, a row watcher and the legacy tracks-v2
  // watcher for the same space would collide on that key: the second
  // `attach` would see "already watching" and never open its own Firestore
  // listener, and detaching either one would tear down both. (Found by
  // adversarial review of an earlier proposal — see
  // .superpowers/sdd/pr4-task-2-brief.md.)
  const { activeWatchers: globalSpaceTracksWatchers, manageWatcher } =
    useWatcherManagementV2<SpaceTrackRow>();

  const manageGlobalSpaceTracksWatchers = async ({
    action,
    watcherKey,
    componentId,
    constraints,
    setEntities,
    limit,
  }: ManageGlobalSpaceTracksWatchersArgs) => {
    if (action === "attach" && !constraints?.length) {
      throw new Error(
        "space-tracks-v1 rows must be queried with a spaceId constraint — an unfiltered list is a silent deny.",
      );
    }
    await manageWatcher({
      watcherType: "multiple",
      action,
      componentId,
      watcherKey,
      collectionId: COLLECTION_ID.SPACE_TRACK,
      ...(action === "attach" && {
        constraints,
        onEntitiesChange: setEntities,
        limit,
      }),
    });
  };

  return (
    <GlobalSpaceTracksContext.Provider
      value={{
        globalSpaceTracks,
        globalSpaceTracksWatchers,
        globalSpaceTracksDispatch,
        manageGlobalSpaceTracksWatchers,
      }}
    >
      {children}
    </GlobalSpaceTracksContext.Provider>
  );
};

export const useGlobalSpaceTracks = () => {
  const context = useContext(GlobalSpaceTracksContext);

  if (!context) {
    throw new Error(
      "useGlobalSpaceTracks must be used within a GlobalSpaceTracksContextProvider",
    );
  }

  return context;
};
